/**
 * AB#23060 (Zendesk#10462) — the factory deadline an order administrator chases.
 *
 * Two dates arrive on an order item, both internal (only for view-factory-deadline):
 *   factory_deadline_on           the date WE gave the factory, set by hand;
 *   factory_reported_dispatch_on  the factory's own planned dispatch date, copied off
 *                                 its latest report (factory_reported_dispatch_key says
 *                                 which factory reported it).
 * The internal date wins when both exist — the backend sorts on the same rule.
 */
export type FactoryDeadlineSource = 'internal' | 'factory';

export interface FactoryDeadlineFields {
  factory_deadline_on?: string | null;
  factory_reported_dispatch_on?: string | null;
  factory_reported_dispatch_key?: string | null;
}

export interface FactoryDeadline {
  /** Y-m-d, or null when neither date exists. */
  date: string | null;
  source: FactoryDeadlineSource | null;
  /** "set by us", or the reporting factory ("RAJCO"). */
  sourceLabel: string;
}

const ymd = (v: unknown): string | null => {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

/** The reporting factory as the order page names it (FactoryReportContext::label). */
export const reportingFactoryLabel = (key: string | null | undefined): string =>
  key && key.trim() ? key.trim().toUpperCase() : 'factory';

export const factoryDeadline = (row: FactoryDeadlineFields | null | undefined): FactoryDeadline => {
  const internal = ymd(row?.factory_deadline_on);
  if (internal) return { date: internal, source: 'internal', sourceLabel: 'set by us' };
  const reported = ymd(row?.factory_reported_dispatch_on);
  if (reported) {
    return {
      date: reported,
      source: 'factory',
      sourceLabel: reportingFactoryLabel(row?.factory_reported_dispatch_key),
    };
  }
  return { date: null, source: null, sourceLabel: '' };
};

/** Statuses past the factory: nothing left to chase, so no tone. */
const DONE = new Set([
  'shipped',
  'completed',
  'order_cancel',
  'waiting_for_consolidation',
  'awaiting_consolidation',
]);

/** Days ahead that already read as "chase now". */
export const FACTORY_DEADLINE_SOON_DAYS = 3;

export type FactoryDeadlineTone = 'overdue' | 'soon' | 'ok' | null;

/** Whole days from `today` to `date` (negative when past); null when either is missing. */
export const daysUntil = (
  date: string | null | undefined,
  today: Date = new Date()
): number | null => {
  const d = ymd(date);
  if (!d) return null;
  const target = new Date(d + 'T00:00:00');
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - t.getTime()) / 86_400_000);
};

export const factoryDeadlineTone = (
  date: string | null | undefined,
  status: string | null | undefined,
  today: Date = new Date()
): FactoryDeadlineTone => {
  const days = daysUntil(date, today);
  if (days === null) return null;
  if (status && DONE.has(status)) return 'ok';
  if (days < 0) return 'overdue';
  if (days <= FACTORY_DEADLINE_SOON_DAYS) return 'soon';
  return 'ok';
};

/** The tooltip: which date it is, whose it is, and how far off. */
export const factoryDeadlineTitle = (
  deadline: FactoryDeadline,
  full: string,
  tone: FactoryDeadlineTone,
  today: Date = new Date()
): string => {
  if (!deadline.date) return 'No factory deadline yet';
  const whose =
    deadline.source === 'internal'
      ? 'the deadline we gave the factory'
      : `${deadline.sourceLabel}'s planned dispatch date (from its report)`;
  const days = daysUntil(deadline.date, today) ?? 0;
  const when =
    tone === 'overdue'
      ? ` — ${-days} day${-days === 1 ? '' : 's'} overdue`
      : days === 0
        ? ' — today'
        : days > 0
          ? ` — in ${days} day${days === 1 ? '' : 's'}`
          : '';
  return `Factory deadline ${full}: ${whose}${tone === 'ok' && days < 0 ? '' : when}`;
};
